import { BadgeInfoComponent } from '@components/shared/badge-info/badge-info.component';
import { PaginationComponent } from '@components/shared/pagination/pagination.component';
import { BadgeInfo } from '@models/badge.model';
import { PaginationEvent } from '@models/pagination.model';
import { TranslateModule } from '@ngx-translate/core';
import { API_ENDPOINTS, ApiService } from '@services/api/api.service';
import { IFlatModel } from '@services/api/flat/flat-model.interface';
import { IssueClientService } from '@services/api/issue/issue-client.service';
import { IIssueModel } from '@services/api/issue/issue-model.interface';
import { ModelLocalizationService } from '@services/formatter/model-localization.service';
import { IssueStatusService } from '@services/util/issue-status.service';
import { LogService } from '@services/util/log.service';
import { FormatDatePipe } from 'app/pipes/format-date.pipe';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Simplified interface for flat issues display
 */
interface FlatIssueRow {
  id: number;
  reportedBy: string;
  reportedDate: Date;
  status: BadgeInfo;
  description: string;
  commentsCount: number;
  updatedDate: Date;
  assignedTo: string;
}

/**
 * Paginated issues response for flat-specific issues
 */
interface PaginatedFlatIssues {
  items: FlatIssueRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

@Component({
  selector: 'app-flat-issues-table',
  standalone: true,
  imports: [CommonModule, TableModule, MessageModule, TranslateModule, PaginationComponent, TagModule, FormatDatePipe, BadgeInfoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Loading Indicator -->
    @if (loading()) {
      <div class="my-6 flex flex-col items-center justify-center">
        <div class="relative h-14 w-14">
          <div class="absolute h-14 w-14 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
        </div>
        <span class="mt-3 font-medium">{{ 'common.loading' | translate }}</span>
      </div>
    }

    @if (error()) {
      <div class="surface-danger border-round mb-3 p-4 text-white">
        {{ error() }}
      </div>
    }

    <!-- Empty state when no issues and not loading -->
    @if (!loading() && (!paginatedIssues() || !paginatedIssues()!.items.length)) {
      <div class="p-5 text-center">
        <i class="pi pi-inbox mb-3 text-4xl text-gray-400"></i>
        <div class="text-xl text-gray-600">{{ 'issues.table.noIssues' | translate }}</div>
        <div class="mt-2 text-sm text-gray-500">{{ 'issues.table.noIssuesDescription' | translate }}</div>
      </div>
    }

    <!-- Table with issues -->
    @if (!loading() && paginatedIssues() && paginatedIssues()!.items.length > 0) {
      <p-table
        [value]="paginatedIssues()!.items"
        [stripedRows]="true"
        styleClass="p-datatable-sm"
        [rowHover]="true"
        [tableStyle]="{ 'min-width': '50rem' }"
        responsiveLayout="scroll"
        breakpoint="1200px"
        dataKey="id"
      >
        <ng-template pTemplate="header">
          <tr>
            <th
              style="vertical-align: middle"
              class="!px-2"
            >
              {{ 'issues.table.tablehead.reportedBy' | translate }}
            </th>
            <th
              style="vertical-align: middle"
              class="!px-2"
            >
              {{ 'issues.table.tablehead.reported' | translate }}
            </th>
            <th
              style="vertical-align: middle"
              class="!px-2"
            >
              {{ 'issues.table.tablehead.status' | translate }}
            </th>
            <th
              style="vertical-align: middle"
              class="!px-2"
            >
              {{ 'issues.table.tablehead.description' | translate }}
            </th>
            <th
              class="!px-2 text-center"
              style="vertical-align: middle"
            >
              {{ 'issues.table.tablehead.comments' | translate }}
            </th>
            <th
              style="vertical-align: middle"
              class="!px-2"
            >
              {{ 'issues.table.tablehead.updated' | translate }}
            </th>
            <th
              style="vertical-align: middle"
              class="!px-2"
            >
              {{ 'issues.table.tablehead.assignedTo' | translate }}
            </th>
          </tr>
        </ng-template>
        <ng-template
          pTemplate="body"
          let-issue
        >
          <tr
            (click)="onIssueClick(issue)"
            class="cursor-pointer"
          >
            <td class="!px-2">
              <div class="flex items-center">
                <div class="ml-2">
                  <div class="font-medium">{{ issue.reportedBy }}</div>
                </div>
              </div>
            </td>
            <td class="!px-2">
              {{ issue.reportedDate | formatDate }}
            </td>
            <td class="!px-2">
              <app-badge-info [badge]="issue.status"></app-badge-info>
            </td>
            <td class="!px-2">
              <div
                class="max-w-xs truncate"
                [title]="issue.description"
              >
                {{ issue.description }}
              </div>
            </td>
            <td class="!px-2 text-center">
              <span class="inline-flex items-center justify-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                {{ issue.commentsCount }}
              </span>
            </td>
            <td class="!px-2">
              {{ issue.updatedDate | formatDate }}
            </td>
            <td class="!px-2">
              <div class="font-medium">{{ issue.assignedTo || '-' }}</div>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Pagination -->
      @if (paginatedIssues() && paginatedIssues()!.totalCount > pageSize) {
        <div class="mt-4">
          <app-pagination
            [currentPage]="currentPage()"
            [totalItems]="paginatedIssues()!.totalCount"
            [pageSize]="pageSize"
            (pageChange)="onPageChange($event)"
          ></app-pagination>
        </div>
      }
    }
  `
})
export class FlatIssuesTableComponent implements OnInit, OnDestroy {
  /**
   * Input signal for the flat to display issues for
   */
  flat = input.required<IFlatModel>();

  /**
   * Signals for component state
   */
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly paginatedIssues = signal<PaginatedFlatIssues | null>(null);
  protected readonly currentPage = signal(1);

  /**
   * Pagination configuration - matches API default
   */
  protected readonly pageSize = 30;

  /**
   * Subject for component destruction
   */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly issueClientService: IssueClientService,
    private readonly apiService: ApiService,
    private readonly log: LogService,
    private readonly issueStatusService: IssueStatusService,
    private readonly localizer: ModelLocalizationService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadFlatIssues();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load issues for the current flat
   */
  private loadFlatIssues(): void {
    const flat = this.flat();
    if (!flat?.id) {
      this.log.warn('FlatIssuesTableComponent: No flat ID provided');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    // Construct the alternate URL for flat-specific issues
    const flatIssuesUrl = this.apiService.getBaseUrl() + API_ENDPOINTS.flat + flat.id + '/issues/';

    this.log.debug('FlatIssuesTableComponent: Loading issues for flat', { flatId: flat.id, url: flatIssuesUrl });

    // Use the issue client service with the alternate URL
    this.issueClientService
      .getItems(flatIssuesUrl, {
        pagination: {
          page: this.currentPage(),
          itemsPerPage: this.pageSize
        },
        order: {
          created_at: 'desc'
        }
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (response) => {
          this.log.debug('FlatIssuesTableComponent: Issues loaded successfully', response);

          const issueRows: FlatIssueRow[] = response.items.map((issue) => this.mapIssueToRow(issue));

          this.paginatedIssues.set({
            items: issueRows,
            totalCount: response.totalItems,
            currentPage: this.currentPage(),
            pageSize: this.pageSize
          });
        },
        error: (error) => {
          this.log.error('FlatIssuesTableComponent: Failed to load issues', error);
          this.error.set('Failed to load issues for this flat');
        }
      });
  }

  /**
   * Map issue model to display row
   */
  private mapIssueToRow(issue: IIssueModel): FlatIssueRow {
    return {
      id: issue.id,
      reportedBy: issue.issuerUser?.name || 'Unknown',
      reportedDate: new Date(issue.created_at || new Date()),
      status: this.mapIssueStatus(issue),
      description: issue.note || 'No description',
      commentsCount: issue.issueNotes?.length || 0,
      updatedDate: new Date(issue.updated_at || issue.created_at || new Date()),
      assignedTo: issue.delegatedUsers?.[0]?.name || ''
    };
  }

  /**
   * Map API issue status to UI badge info (same logic as original issues table)
   */
  private mapIssueStatus(issue: IIssueModel): BadgeInfo {
    const badgeInfo = {
      id: issue.issueStatus ? issue.issueStatus.id : 0,
      text: this.localizer.getIssueStatusKey(issue.issueStatus || null),
      iconClasses: this.issueStatusService.getStatusIconClass(issue.issueStatus || null),
      colorClasses: this.issueStatusService.getStatusColorClasses(issue.issueStatus || null)
    };
    return badgeInfo;
  }

  /**
   * Handle page change events
   */
  protected onPageChange(event: PaginationEvent): void {
    this.currentPage.set(event.page);
    this.loadFlatIssues();
  }

  /**
   * Handle issue row click - navigate to issue details page
   */
  protected onIssueClick(issue: FlatIssueRow): void {
    this.log.debug('FlatIssuesTableComponent: Navigating to issue details', { issueId: issue.id });
    this.router.navigate(['/issues', issue.id]);
  }
}
